import { ChevronRight } from '@mui/icons-material';
import { Radio, RadioGroup } from '@mui/material';
import { connect, FormikContextType, getIn } from 'formik';
import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BORDER_COLOR,
  CHARCOAL_GREY,
  GREY,
  LIGHT_BLUE,
  VERTICAL_SEPARATOR,
  WHITE,
} from 'src/Constants/Colors.constants';
import { Button, FormControlLabel, List, Typography } from 'src/UIKit';
import { PopoverToModal } from '~/Components';
import { Group } from '~/Components/Icons/Group';
import { FONT_SIZE_LARGISH, FONT_SIZE_MEDIUM } from '~/Constants/UI.constants';
import { useBoolean } from '~/Hooks';
import ListItemSelect from '~/Screens/Events/EventPage/Components/ListItemSelect';
import { makeStyles } from '~/UIKit/Style';
import { ExtendedStaff } from '../../ExtendedSaff.model';

interface StaffRoleSelectProps {
  isOwner?: boolean;
}

const useStyles = makeStyles(
  () => ({
    inputWrapper: {
      display: 'flex',
      alignItems: 'center',
      background: WHITE,
      minHeight: 50,
      justifyContent: 'space-between',
      padding: '0 7px 0 15px',
      borderBottom: `thin solid ${VERTICAL_SEPARATOR}`,
      borderTop: `thin solid ${VERTICAL_SEPARATOR}`,
      position: 'relative',
      opacity: 1,
      fontSize: FONT_SIZE_MEDIUM,
    },
    label: {
      flex: 'auto',
      fontSize: FONT_SIZE_MEDIUM,
      lineHeight: '23px',
    },
    button: {
      color: GREY,
      fontWeight: 'normal',
    },
    iconWrapper: {
      backgroundColor: LIGHT_BLUE,
      borderRadius: 30,
      minWidth: 30,
      maxWidth: 30,
      minHeight: 30,
      maxHeight: 30,
      lineHeight: '15px',
      fontSize: FONT_SIZE_LARGISH,
      display: 'flex',
      alignItems: 'center',
      placeContent: 'center',
      marginRight: 15,
      color: CHARCOAL_GREY,
      '& > svg': {
        fontSize: 'inherit',
      },
    },
    staffList: {
      borderTop: `1px solid ${BORDER_COLOR}`,
      padding: 0,
    },
    radioGroup: {
      marginLeft: 'auto',
    },
    radio: {
      marginRight: 0,
    },
  }),
  { name: 'StaffRoleSelect' },
);

export const StaffRoleSelect = connect<StaffRoleSelectProps, ExtendedStaff>(
  ({ isOwner, formik }: StaffRoleSelectProps & { formik: FormikContextType<ExtendedStaff> }) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { classes } = useStyles();
    const { t } = useTranslation();
    const { setTrue: open, setFalse: close, state: isOpen } = useBoolean(false);
    const defaultSelectedRole = getIn(formik.values, 'accessType');

    const updateSelection = (accessType: number) => () => {
      formik.setFieldValue('accessType', accessType);
      close();
    };

    const selectedRole = useMemo(() => t(`settings.staff.accessType.${defaultSelectedRole}`), [
      defaultSelectedRole,
    ]);

    return (
      <div className={classes.inputWrapper}>
        <div className={classes.iconWrapper}>
          <Group />
        </div>
        <Typography className={classes.label}>{t('app.role')}</Typography>
        <Button disabled={isOwner} ref={buttonRef} className={classes.button} onClick={open}>
          <span>{selectedRole ? selectedRole : t('app.add')}</span>
          <ChevronRight />
        </Button>
        <PopoverToModal
          actionName={t('app.save')}
          anchorEl={buttonRef.current}
          title={t('app.role')}
          open={isOpen}
          onClose={close}
          onActionTaken={close}
        >
          <List className={classes.staffList} data-testid="ChangeLocationList">
            {[0, 1, 2, 3].map((roleID) => (
              <ListItemSelect
                key={roleID}
                onClick={updateSelection(roleID)}
                name={t(`settings.staff.accessType.${roleID}`)}
                testId={'ChangeLocationListItem'}
              >
                <RadioGroup
                  aria-label="location"
                  name="location"
                  value={defaultSelectedRole}
                  onChange={updateSelection(roleID)}
                  className={classes.radioGroup}
                >
                  <FormControlLabel
                    value={roleID}
                    control={<Radio />}
                    label=""
                    className={classes.radio}
                    checked={roleID === defaultSelectedRole}
                  />
                </RadioGroup>
              </ListItemSelect>
            ))}
          </List>
        </PopoverToModal>
      </div>
    );
  },
);
